<template>
  <section class="bulk-maintenance" aria-live="polite">
    <header class="bulk-maintenance-head">
      <div>
        <span class="bulk-kicker">Bulk maintenance</span>
        <strong>{{ parts.length }} part{{ parts.length === 1 ? '' : 's' }} selected</strong>
        <small>The backend owns this batch, so it can continue if you leave this page.</small>
      </div>
      <button type="button" class="bulk-close" :disabled="busy" @click="$emit('clear')">Clear selection</button>
    </header>

    <div v-if="actionsLoading" class="bulk-state">Checking the available actions for these parts&hellip;</div>
    <div v-else-if="actionsError" class="bulk-state is-error">
      <span>{{ actionsError }}</span>
      <button type="button" @click="loadActions">Try again</button>
    </div>
    <div v-else-if="!actions.length" class="bulk-state">
      These parts do not share a maintenance action.
    </div>
    <div v-else class="bulk-controls">
      <label class="bulk-field">
        <span>Action</span>
        <select v-model="selectedActionKey" :disabled="busy">
          <option v-for="action in actions" :key="action.key" :value="action.key">
            {{ action.label || prettyLabel(action.key) }}
          </option>
        </select>
      </label>

      <label v-if="selectedActionKey === 'auto-fix'" class="bulk-preview">
        <input v-model="dryRun" type="checkbox" :disabled="busy">
        <span><strong>Preview only</strong><small>Do not write deterministic fixes.</small></span>
      </label>

      <button type="button" class="bulk-run" :disabled="busy || !selectedActionKey" @click="runBatch">
        {{ runButtonLabel }}
      </button>
    </div>

    <p v-if="selectedAction && selectedAction.description && !run" class="bulk-description">
      {{ selectedAction.description }}
    </p>

    <div v-if="run || message || error" class="bulk-run-state">
      <div class="bulk-run-summary">
        <span class="bulk-status" :class="'tone-' + statusTone">{{ statusLabel }}</span>
        <span v-if="runId(run)">Run #{{ runId(run) }}</span>
        <span>{{ parts.length }} parts</span>
      </div>
      <div v-if="progress !== null" class="bulk-progress"><span :style="{ width: progress + '%' }"></span></div>
      <p v-if="message" class="bulk-message">{{ message }}</p>
      <p v-if="error" class="bulk-error">{{ error }}</p>
      <details v-if="error || logs.length" class="bulk-details">
        <summary>Job details <span v-if="logs.length">{{ logs.length }} log entries</span></summary>
        <div>
          <pre v-if="error">{{ error }}</pre>
          <pre v-for="(line, index) in logs" :key="index">{{ line }}</pre>
        </div>
      </details>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../../config'
import realtime from '../../../services/realtime'

export default {
  name: 'BulkPartMaintenancePanel',
  props: {
    parts: { type: Array, default: function () { return [] } }
  },
  data () {
    return {
      actions: [],
      actionCache: {},
      actionsLoading: false,
      actionsError: '',
      actionsRequestId: 0,
      selectedActionKey: '',
      dryRun: true,
      starting: false,
      refreshing: false,
      run: null,
      message: '',
      error: '',
      pollTimer: null,
      trackingCleanups: []
    }
  },
  computed: {
    selectedAction () {
      return this.actions.find((action) => action.key === this.selectedActionKey) || null
    },
    busy () {
      return this.starting || this.refreshing || this.isRunActive(this.run)
    },
    progress () {
      return this.runProgress(this.run)
    },
    logs () {
      return this.runLogs(this.run)
    },
    statusLabel () {
      if (this.starting) return 'Starting'
      if (this.refreshing) return 'Refreshing parts'
      if (!this.run) return 'Ready'
      return this.prettyLabel(this.runStatus(this.run))
    },
    statusTone () {
      if (this.error || (this.run && this.isTerminalRun(this.run) && !this.runSucceeded(this.run))) return 'bad'
      if (this.busy) return 'active'
      if (this.run && this.runSucceeded(this.run)) return 'good'
      return 'idle'
    },
    runButtonLabel () {
      if (this.starting) return 'Starting batch...'
      if (this.refreshing) return 'Refreshing parts...'
      if (this.isRunActive(this.run)) {
        return this.progress === null ? 'Batch running...' : `Batch ${Math.round(this.progress)}%`
      }
      if (this.selectedActionKey === 'auto-fix') return this.dryRun ? 'Preview selected' : 'Apply to selected'
      return 'Run on selected'
    }
  },
  watch: {
    busy: {
      immediate: true,
      handler (value) {
        this.$emit('busy-change', value)
      }
    },
    parts: {
      immediate: true,
      handler () {
        this.loadActions()
      }
    }
  },
  beforeDestroy () {
    this.cleanupTracking()
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    catalogType (type) {
      const normalized = String(type || '').trim().toLowerCase()
      if (normalized === 'graphics') return 'gpu'
      if (normalized === 'processor') return 'cpu'
      return normalized
    },
    partId (part) {
      return part && (part.id || part.partId || part.part_id)
    },
    partKey (part) {
      return `${this.catalogType(part && part.type)}:${this.partId(part)}`
    },
    responsePayload (data) {
      return data && data.data && typeof data.data === 'object' ? data.data : (data || {})
    },
    async loadActions () {
      const parts = (this.parts || []).filter((part) => this.partId(part) && this.catalogType(part.type))
      const requestId = ++this.actionsRequestId
      this.actionsError = ''
      if (!parts.length) {
        this.actions = []
        this.selectedActionKey = ''
        return
      }
      this.actionsLoading = true
      try {
        const actionLists = []
        for (let offset = 0; offset < parts.length; offset += 6) {
          const chunk = parts.slice(offset, offset + 6)
          const results = await Promise.all(chunk.map((part) => this.actionsForPart(part)))
          actionLists.push.apply(actionLists, results)
        }
        if (requestId !== this.actionsRequestId) return
        const first = actionLists[0] || []
        this.actions = first.filter((action) => {
          return action && action.key && actionLists.every((list) => list.some((candidate) => candidate.key === action.key))
        })
        if (!this.actions.some((action) => action.key === this.selectedActionKey)) {
          this.selectedActionKey = this.actions[0] ? this.actions[0].key : ''
        }
      } catch (error) {
        if (requestId !== this.actionsRequestId) return
        this.actions = []
        this.selectedActionKey = ''
        this.actionsError = this.errorMessage(error)
      } finally {
        if (requestId === this.actionsRequestId) this.actionsLoading = false
      }
    },
    async actionsForPart (part) {
      const key = this.partKey(part)
      if (this.actionCache[key]) return this.actionCache[key]
      const { data } = await axios.get(
        `${apiDomain}catalog-parts/${encodeURIComponent(this.catalogType(part.type))}/${encodeURIComponent(this.partId(part))}`,
        { headers: this.authHeaders() }
      )
      const payload = this.responsePayload(data)
      const actions = Array.isArray(payload.maintenanceActions)
        ? payload.maintenanceActions
        : (Array.isArray(payload.maintenance_actions) ? payload.maintenance_actions : [])
      this.$set(this.actionCache, key, actions)
      return actions
    },
    async runBatch () {
      if (this.busy || !this.selectedActionKey || !this.parts.length) return
      const count = this.parts.length
      if (this.selectedActionKey === 'refresh-images' && !window.confirm(
        `Re-scrape images for ${count} selected part${count === 1 ? '' : 's'}?\n\nThis replaces each part's current server-side image set.`
      )) return
      if (this.selectedActionKey === 'auto-fix' && !this.dryRun && !window.confirm(
        `Apply deterministic fixes to ${count} selected part${count === 1 ? '' : 's'}?\n\nThis writes changes to every selected catalog record.`
      )) return

      this.cleanupTracking()
      this.starting = true
      this.refreshing = false
      this.run = null
      this.message = ''
      this.error = ''
      try {
        const body = {
          parts: this.parts.map((part) => ({
            type: this.catalogType(part.type),
            id: this.partId(part)
          }))
        }
        if (this.selectedActionKey === 'auto-fix') body.dryRun = this.dryRun
        const { data } = await axios.post(
          `${apiDomain}catalog-parts/maintenance/${encodeURIComponent(this.selectedActionKey)}/batch`,
          body,
          { headers: this.authHeaders() }
        )
        const payload = this.responsePayload(data)
        const run = this.extractRun(payload)
        const started = payload.started !== undefined ? !!payload.started : !!run
        const reason = String(payload.reason || '').toLowerCase()
        this.starting = false
        this.run = run

        if (!started && reason !== 'already-running') {
          this.error = payload.message || payload.reason || 'The bulk maintenance job did not start.'
          return
        }
        this.message = reason === 'already-running'
          ? 'This batch is already running. Showing the active run.'
          : 'Bulk maintenance started.'
        if (!run || !this.runId(run)) {
          this.error = 'The batch response did not include a job run to track.'
          return
        }
        if (this.isTerminalRun(run)) this.finishRun(run)
        else this.trackRun(run)
      } catch (error) {
        this.starting = false
        this.error = this.errorMessage(error)
      }
    },
    trackRun (run) {
      this.run = run
      const id = this.runId(run)
      const topic = `jobs:${id}`
      this.trackingCleanups.push(realtime.subscribe(topic, {
        'job:snapshot': this.handleRunEvent,
        'job:progress': this.handleRunEvent,
        'job:log': this.handleRunEvent,
        'job:finished': this.handleRunEvent,
        'job:error': this.handleRunError
      }))
      this.trackingCleanups.push(realtime.onStateChange((state, connected, detail) => {
        if (connected && state === 'open') {
          this.clearPoll()
          this.error = ''
          realtime.refresh(topic)
        } else if (!connected) {
          if (state === 'error') this.error = realtime.errorMessage(detail)
          this.schedulePoll(500)
        }
      }))
      if (realtime.isConnected()) realtime.refresh(topic)
      else this.schedulePoll(500)
    },
    handleRunEvent (payload) {
      const incoming = this.extractRun(payload) || payload || {}
      const logs = this.runLogs(this.run).concat(this.runLogs(payload), this.runLogs(incoming))
        .filter((line, index, values) => values.indexOf(line) === index).slice(-60)
      this.run = Object.assign({}, this.run || {}, incoming, logs.length ? { logs } : {})
      if (this.isTerminalRun(this.run)) this.finishRun(this.run)
    },
    handleRunError (payload) {
      const data = payload || {}
      const run = this.extractRun(data)
      if (run) this.handleRunEvent(data)
      else this.error = data.message || data.reason || realtime.errorMessage(data)
    },
    schedulePoll (delay) {
      this.clearPoll()
      this.pollTimer = window.setTimeout(this.pollRun, delay || 2500)
    },
    clearPoll () {
      if (this.pollTimer) window.clearTimeout(this.pollTimer)
      this.pollTimer = null
    },
    async pollRun () {
      this.clearPoll()
      const id = this.runId(this.run)
      if (!id || this.isTerminalRun(this.run)) return
      try {
        const { data } = await axios.get(`${apiDomain}jobs/runs/${encodeURIComponent(id)}`, { headers: this.authHeaders() })
        this.handleRunEvent(data)
        if (!this.isTerminalRun(this.run)) this.schedulePoll(2500)
      } catch (error) {
        this.error = `Could not refresh batch status: ${this.errorMessage(error)}`
        this.schedulePoll(3500)
      }
    },
    finishRun (run) {
      this.cleanupTracking()
      this.run = run
      if (!this.runSucceeded(run)) {
        this.error = this.runError(run) || `Batch ${this.prettyLabel(this.runStatus(run))}.`
        return
      }
      this.refreshing = true
      this.message = 'Batch completed. Refreshing affected catalog rows...'
      this.$emit('completed', {
        action: this.selectedActionKey,
        run,
        complete: (error) => {
          this.refreshing = false
          this.message = error ? 'The batch completed, but the catalog list could not be refreshed.' : 'Batch completed. Catalog rows are up to date.'
          this.error = error ? this.errorMessage(error) : ''
          if (!error) this.actionCache = {}
        }
      })
    },
    cleanupTracking () {
      this.clearPoll()
      this.trackingCleanups.forEach((cleanup) => {
        if (typeof cleanup === 'function') cleanup()
      })
      this.trackingCleanups = []
    },
    extractRun (data) {
      if (!data) return null
      if (data.run) return data.run
      if (data.batchRun) return data.batchRun
      if (data.batch_run) return data.batch_run
      if (data.activeRun) return data.activeRun
      if (data.active_run) return data.active_run
      if (data.jobRun) return data.jobRun
      if (data.job_run) return data.job_run
      if (data.data) return this.extractRun(data.data)
      if (data.id || data.runId || data.run_id) return data
      return null
    },
    runId (run) {
      return run && (run.id || run.runId || run.run_id)
    },
    runStatus (run) {
      return String((run && (run.status || run.state)) || 'queued').toLowerCase()
    },
    isRunActive (run) {
      return !!run && ['starting', 'queued', 'pending', 'running', 'active'].indexOf(this.runStatus(run)) >= 0
    },
    isTerminalRun (run) {
      return ['success', 'succeeded', 'completed', 'complete', 'failed', 'error', 'cancelled', 'canceled'].indexOf(this.runStatus(run)) >= 0
    },
    runSucceeded (run) {
      return ['success', 'succeeded', 'completed', 'complete'].indexOf(this.runStatus(run)) >= 0
    },
    runProgress (run) {
      if (!run) return null
      const raw = run.percent !== undefined ? run.percent
        : run.progressPercent !== undefined ? run.progressPercent
          : run.progress_percent !== undefined ? run.progress_percent
            : typeof run.progress === 'number' ? run.progress : null
      if (raw === null || raw === undefined || raw === '') return null
      const number = Number(raw)
      if (!isFinite(number)) return null
      return Math.max(0, Math.min(100, number <= 1 ? number * 100 : number))
    },
    runLogs (run) {
      if (!run) return []
      const raw = run.logs || run.log || run.logAppend || run.log_append || run.messages || run.events || []
      return (Array.isArray(raw) ? raw : [raw]).map((entry) => {
        if (entry === null || entry === undefined) return ''
        if (typeof entry === 'string') return entry
        return entry.message || entry.text || entry.log || entry.detail || JSON.stringify(entry)
      }).reduce((lines, entry) => lines.concat(String(entry || '').split(/\r?\n/)), [])
        .filter((entry) => entry.trim()).slice(-60)
    },
    runError (run) {
      if (!run) return ''
      const result = run.result || run.output || run.stats || {}
      return run.error || run.lastError || run.last_error || result.error || result.message || ''
    },
    prettyLabel (value) {
      return String(value || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase())
    },
    errorMessage (error) {
      return (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
        (error && error.message) || 'Request failed.'
    }
  }
}
</script>

<style scoped>
.bulk-maintenance {
  margin: 0 0 12px;
  padding: 13px 14px;
  border: 1px solid rgba(25, 151, 198, 0.5);
  border-radius: 8px;
  background: radial-gradient(circle at 5% 0%, rgba(25, 151, 198, 0.13), transparent 30%), rgba(8, 12, 18, 0.92);
  color: #cfd4df;
}
.bulk-maintenance-head, .bulk-controls, .bulk-run-summary { display: flex; align-items: center; gap: 12px; }
.bulk-maintenance-head { justify-content: space-between; margin-bottom: 11px; }
.bulk-maintenance-head > div { display: grid; gap: 2px; }
.bulk-kicker { color: #20bff3; font-size: 8pt; font-weight: 900; letter-spacing: .07em; text-transform: uppercase; }
.bulk-maintenance-head strong { color: #e4e9f1; font-size: 11pt; }
.bulk-maintenance-head small, .bulk-description { color: #818da0; font-size: 8pt; }
.bulk-close, .bulk-run, .bulk-state button {
  min-height: 34px; padding: 0 11px; border: 1px solid rgba(67,72,87,.85); border-radius: 6px;
  background: rgba(10,15,23,.78); color: #bfc8d7; cursor: pointer; font-size: 8pt; font-weight: 900;
}
.bulk-controls { align-items: flex-end; }
.bulk-field { display: grid; flex: 1 1 280px; gap: 4px; margin: 0; color: #8f9aad; font-size: 8pt; font-weight: 800; }
.bulk-field select { width: 100%; min-height: 36px; padding: 0 9px; border: 1px solid rgba(67,72,87,.9); border-radius: 6px; background: #080c12; color: #dce2ec; }
.bulk-preview { display: flex; align-items: center; gap: 7px; min-height: 36px; margin: 0; padding: 5px 9px; border: 1px solid rgba(224,192,126,.34); border-radius: 6px; }
.bulk-preview span { display: grid; } .bulk-preview strong { color: #e0c07e; font-size: 8pt; } .bulk-preview small { color: #8f826d; font-size: 7pt; }
.bulk-run { border-color: rgba(25,151,198,.7); background: rgba(25,151,198,.13); color: #a9e7fb; min-width: 150px; }
.bulk-close:disabled, .bulk-run:disabled { cursor: wait; opacity: .55; }
.bulk-state { padding: 12px; border: 1px dashed rgba(67,72,87,.64); border-radius: 6px; color: #8d98aa; font-size: 8.5pt; }
.bulk-state.is-error, .bulk-error { color: #f3a3b1; }
.bulk-state button { float: right; min-height: 26px; }
.bulk-description { margin: 8px 0 0; }
.bulk-run-state { display: grid; gap: 6px; margin-top: 10px; padding-top: 9px; border-top: 1px solid rgba(67,72,87,.55); }
.bulk-run-summary { color: #778297; font-size: 7.5pt; }
.bulk-status { padding: 2px 7px; border: 1px solid rgba(67,72,87,.7); border-radius: 999px; font-weight: 900; text-transform: uppercase; }
.bulk-status.tone-active { border-color: rgba(25,151,198,.55); color: #9bdff5; } .bulk-status.tone-good { border-color: rgba(27,201,142,.5); color: #8fe8c4; } .bulk-status.tone-bad { border-color: rgba(232,79,106,.5); color: #f49aaa; }
.bulk-progress { height: 4px; overflow: hidden; border-radius: 999px; background: rgba(67,72,87,.45); }
.bulk-progress span { display: block; height: 100%; border-radius: inherit; background: #16b4ed; transition: width .2s ease; }
.bulk-message, .bulk-error { margin: 0; font-size: 8pt; }
.bulk-message { color: #9aacc0; }
.bulk-details { overflow: hidden; border: 1px solid rgba(67,72,87,.58); border-radius: 6px; background: rgba(3,5,9,.4); }
.bulk-details summary { display: flex; justify-content: space-between; padding: 7px 8px; color: #9ba6b8; cursor: pointer; font-size: 7.5pt; font-weight: 900; }
.bulk-details > div { max-height: 150px; padding: 7px 8px; overflow: auto; border-top: 1px solid rgba(67,72,87,.48); }
.bulk-details pre { margin: 0 0 4px; color: #9da8ba; font: 7.2pt/1.4 monospace; white-space: pre-wrap; word-break: break-word; }
@media (max-width: 700px) {
  .bulk-maintenance-head, .bulk-controls { align-items: stretch; flex-direction: column; }
  .bulk-field { flex-basis: auto; } .bulk-run, .bulk-close { width: 100%; }
}
</style>
